import React, { useState, useEffect } from 'react';
import { User, Clock } from 'lucide-react';
import api from '@/lib/api';

/**
 * Small badge displayed at the bottom of any entity detail view.
 *
 * Two usage modes:
 *   1) Provide a `doc` already containing created_by/updated_by metadata.
 *   2) Provide `entityType` + `entityId` — the badge will auto-fetch the
 *      last-touched info from the global activity log.
 *
 *   <LastTouchedBadge doc={devisDoc} />
 *   <LastTouchedBadge entityType="devis" entityId={devisId} />
 */
export default function LastTouchedBadge({ doc, entityType, entityId, compact = false }) {
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (doc) return;
    if (!entityType || !entityId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/last-touched/${entityType}/${entityId}`);
        if (!cancelled) setFetched(r.data);
      } catch (_e) {
        if (!cancelled) setFetched({});
      }
    })();
    return () => { cancelled = true; };
  }, [doc, entityType, entityId]);

  const source = doc || fetched;
  if (!source) return null;

  const createdBy = source.created_by;
  const updatedBy = source.updated_by;
  const createdAt = source.created_at;
  const updatedAt = source.updated_at;
  if (!createdBy && !updatedBy && !createdAt && !updatedAt) return null;

  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (_e) { return iso; }
  };

  const userLabel = (u) => {
    if (!u) return '—';
    if (typeof u === 'string') return u;
    return u.name || u.email || 'Inconnu';
  };

  if (compact) {
    return (
      <div className="text-xs text-slate-400 flex items-center gap-1" data-testid="last-touched-badge">
        <Clock className="w-3 h-3" />
        Modifié par <span className="font-medium text-slate-600">{userLabel(updatedBy || createdBy)}</span>
        {updatedAt && <span>· {fmt(updatedAt)}</span>}
      </div>
    );
  }

  return (
    <div className="mt-6 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-4 text-xs text-slate-500" data-testid="last-touched-badge">
      {createdBy && (
        <div className="flex items-center gap-1.5">
          <User className="w-3 h-3" />
          Créé par <span className="font-semibold text-slate-700">{userLabel(createdBy)}</span>
          {createdAt && <span className="text-slate-400">le {fmt(createdAt)}</span>}
        </div>
      )}
      {updatedBy && (updatedAt !== createdAt) && (
        <div className="flex items-center gap-1.5">
          <Clock className="w-3 h-3" />
          Dernière modification par <span className="font-semibold text-slate-700">{userLabel(updatedBy)}</span>
          {updatedAt && <span className="text-slate-400">le {fmt(updatedAt)}</span>}
        </div>
      )}
    </div>
  );
}
